import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Wallet, 
  Shield, 
  Activity,
  TrendingUp,
  Clock,
  DollarSign,
  FileCheck,
  RefreshCw,
  AlertCircle,
  BarChart3,
  ChevronRight,
  Database,
  Cpu,
  MemoryStick,
  HardDrive
} from 'lucide-react';
import { AdminSystemAPI, AdminAPIUtils } from '../services/api';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recentActivity, setRecentActivity] = useState([]);
  const [quickStats, setQuickStats] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await AdminSystemAPI.getSystemStats();
      
      if (response.success && response.data) {
        const data = response.data;
        setStats(data);
        setLastRefresh(new Date());
        
        if (data.recent_activities && data.recent_activities.length > 0) {
          const filteredActivities = data.recent_activities.filter(activity => 
            activity.created_at || activity.time_ago
          ).slice(0, 5);
          setRecentActivity(filteredActivities);
        }
        
        calculateQuickStats(data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(AdminAPIUtils.handleError(err, false).message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const calculateQuickStats = (data) => {
    if (!data) return;
    
    const today = new Date().toISOString().split('T')[0];
    
    let depositsToday = 0;
    let withdrawalsToday = 0;
    let kycApprovedToday = 0;
    
    if (data.wallet?.daily_volume) {
      const todayData = data.wallet.daily_volume.find(item => item.date === today);
      if (todayData) {
        depositsToday = parseFloat(todayData.deposits) || 0;
        withdrawalsToday = parseFloat(todayData.withdrawals) || 0;
      }
    }
    
    if (data.kyc?.daily_submissions) {
      const todayKYC = data.kyc.daily_submissions.find(item => item.date === today);
      if (todayKYC) {
        kycApprovedToday = parseInt(todayKYC.approved) || 0;
      }
    }
    
    const quickStatsData = {
      newUsersToday: data.users?.new_today || 0,
      depositsToday: depositsToday,
      withdrawalsToday: withdrawalsToday,
      kycApprovedToday: kycApprovedToday,
      activeSessions: data.users?.active_sessions || 0,
      pendingDeposits: parseFloat(data.wallet?.pending_deposits) || 0,
      pendingWithdrawals: parseFloat(data.wallet?.pending_withdrawals) || 0,
      houseEdge: data.games?.house_edge || 'N/A',
      totalUsers: data.users?.total_users || 0,
      totalBalance: parseFloat(data.wallet?.total_balance) || 0,
      pendingKYC: data.kyc?.pending || 0
    };
    
    setQuickStats(quickStatsData);
  };

  const formatBTC = (amount) => {
    if (typeof amount === 'string') {
      const num = parseFloat(amount);
      return num.toFixed(8);
    }
    return amount || '0.00000000';
  };

  const formatCurrency = (amount) => {
    if (typeof amount === 'string') {
      const num = parseFloat(amount);
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(num);
    }
    return amount || '0.00';
  };

  const formatActivityType = (type) => {
    switch(type) {
      case 'user_registration':
        return 'New user registered';
      case 'deposit':
        return 'Deposit received';
      case 'withdrawal':
        return 'Withdrawal processed';
      case 'kyc_approval':
        return 'KYC approved';
      default:
        return 'System activity';
    }
  };

  const formatActivityIcon = (type) => {
    switch(type) {
      case 'user_registration':
        return <Users className="w-4 h-4 text-emerald-400" />;
      case 'deposit':
        return <DollarSign className="w-4 h-4 text-sky-400" />;
      case 'withdrawal':
        return <Wallet className="w-4 h-4 text-amber-400" />;
      case 'kyc_approval':
        return <FileCheck className="w-4 h-4 text-violet-400" />;
      default:
        return <Activity className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatActivityUser = (activity) => {
    if (activity.username) {
      return activity.username;
    } else if (activity.email) {
      return activity.email.split('@')[0];
    }
    return 'User';
  };

  const formatActivityAmount = (activity) => {
    if (activity.amount) {
      return `${formatBTC(activity.amount)} BTC`;
    }
    return '';
  };

  const getGrowthRateColor = (rate) => {
    if (rate > 0) return 'text-emerald-400';
    if (rate < 0) return 'text-rose-400';
    return 'text-gray-400';
  };

  const getGrowthRateIcon = (rate) => {
    if (rate > 0) return <TrendingUp className="w-4 h-4" />;
    if (rate < 0) return <TrendingUp className="w-4 h-4 rotate-180" />;
    return <BarChart3 className="w-4 h-4" />;
  };

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.users?.total_users || 0,
      icon: <Users className="w-6 h-6" />,
      color: 'from-sky-500 to-blue-600',
      change: stats?.users?.new_today || 0,
      changeText: 'new today',
      trend: stats?.users?.new_today > 0 ? 'up' : 'neutral'
    },
    {
      title: 'Total Balance',
      value: `${formatBTC(stats?.wallet?.total_balance || 0)} BTC`,
      icon: <Wallet className="w-6 h-6" />,
      color: 'from-emerald-500 to-green-600',
      change: `${formatBTC(stats?.wallet?.total_deposits || 0)} BTC`,
      changeText: 'total deposits',
      trend: 'up'
    },
    {
      title: 'Pending KYC',
      value: stats?.kyc?.pending || 0,
      icon: <Shield className="w-6 h-6" />,
      color: 'from-amber-500 to-yellow-600',
      change: stats?.kyc?.total || 0,
      changeText: 'total submissions',
      trend: 'neutral'
    },
    {
      title: 'Active Sessions',
      value: stats?.users?.active_sessions || 0,
      icon: <Activity className="w-6 h-6" />,
      color: 'from-violet-500 to-purple-600',
      change: `${stats?.users?.growth_rate || 0}%`,
      changeText: 'growth rate',
      trend: stats?.users?.growth_rate > 0 ? 'up' : stats?.users?.growth_rate < 0 ? 'down' : 'neutral'
    },
  ];

  const quickStatsItems = [
    {
      label: 'New users today',
      value: quickStats?.newUsersToday || 0,
      icon: <Users className="w-4 h-4" />,
      color: 'text-emerald-400'
    },
    {
      label: 'Deposits today',
      value: `${formatBTC(quickStats?.depositsToday || 0)} BTC`,
      icon: <DollarSign className="w-4 h-4" />,
      color: 'text-sky-400'
    },
    {
      label: 'Withdrawals today',
      value: `${formatBTC(quickStats?.withdrawalsToday || 0)} BTC`,
      icon: <Wallet className="w-4 h-4" />,
      color: 'text-amber-400'
    },
    {
      label: 'KYC approved today',
      value: quickStats?.kycApprovedToday || 0,
      icon: <FileCheck className="w-4 h-4" />,
      color: 'text-violet-400'
    },
    {
      label: 'Active sessions',
      value: quickStats?.activeSessions || 0,
      icon: <Activity className="w-4 h-4" />,
      color: 'text-indigo-400'
    },
    {
      label: 'Pending deposits',
      value: `${formatBTC(quickStats?.pendingDeposits || 0)} BTC`,
      icon: <Clock className="w-4 h-4" />,
      color: 'text-yellow-400'
    },
    {
      label: 'Pending withdrawals',
      value: `${formatBTC(quickStats?.pendingWithdrawals || 0)} BTC`,
      icon: <Clock className="w-4 h-4" />,
      color: 'text-rose-400'
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-deep-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-night-blue mx-auto mb-6"></div>
          <p className="text-charcoal-gray">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-black text-gray-100 p-6 space-y-6">
      {/* Header Section */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
            Dashboard Overview
          </h1>
          {lastRefresh && (
            <div className="flex items-center mt-2 text-charcoal-gray">
              <Clock className="w-4 h-4 mr-2" />
              <span className="text-sm">Last updated: {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          )}
        </div>
        <button 
          onClick={fetchDashboardData}
          className="px-5 py-2.5 bg-night-blue hover:bg-blue-800 text-white rounded-xl transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh Data
        </button>
      </div>
      
      {/* Error Alert */}
      {error && (
        <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl backdrop-blur-sm">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-rose-400 mr-3" />
            <div>
              <p className="text-rose-300 font-medium">Error</p>
              <p className="text-sm text-rose-200/80 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div 
            key={index} 
            className="bg-night-blue/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:bg-night-blue/70 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl group"
          >
            <div className="flex items-start justify-between mb-6">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${card.color} shadow-lg`}>
                {card.icon}
              </div>
              {card.trend === 'up' && (
                <div className="flex items-center text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">+{card.change}</span>
                </div>
              )}
              {card.trend === 'down' && (
                <div className="flex items-center text-rose-400 bg-rose-400/10 px-3 py-1 rounded-full">
                  <TrendingUp className="w-4 h-4 mr-1 rotate-180" />
                  <span className="text-sm font-medium">{card.change}</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold text-white">{card.value}</p>
              <p className="text-charcoal-gray text-sm uppercase tracking-wider">{card.title}</p>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-800">
              <p className="text-sm text-charcoal-gray">
                <span className={`font-medium ${card.trend === 'up' ? 'text-emerald-400' : card.trend === 'down' ? 'text-rose-400' : 'text-gray-400'}`}>
                  {card.trend === 'up' ? '+' : ''}{card.change}
                </span>{' '}
                {card.changeText}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Recent Activity */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-bold text-white">Recent Activity</h2>
              <div className="flex items-center text-charcoal-gray text-sm bg-gray-900/50 px-3 py-1 rounded-full">
                <span>{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
              </div>
            </div>
            
            {recentActivity.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.map((activity, index) => (
                  <div 
                    key={index} 
                    className="flex items-center p-4 bg-gray-900/30 hover:bg-gray-800/50 rounded-xl transition-all duration-300 group"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center shadow-lg">
                        {formatActivityIcon(activity.type)}
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-white group-hover:text-gray-100">
                          {formatActivityType(activity.type)}
                        </p>
                        {formatActivityAmount(activity) && (
                          <span className="font-semibold text-sky-300">
                            {formatActivityAmount(activity)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center text-charcoal-gray text-sm">
                          <Users className="w-3 h-3 mr-1" />
                          <span>{formatActivityUser(activity)}</span>
                        </div>
                        <span className="text-sm text-charcoal-gray">
                          {activity.time_ago || 'Just now'}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-600 ml-3 group-hover:text-gray-400" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Activity className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                <p className="text-charcoal-gray">No recent activity</p>
              </div>
            )}
          </div>

          {/* Transaction Volume */}
          {stats?.wallet?.daily_volume && stats.wallet.daily_volume.length > 0 && (
            <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-8">Transaction Volume (7D)</h2>
              <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
                {stats.wallet.daily_volume.slice(-7).map((day, index) => (
                  <div key={index} className="text-center">
                    <p className="text-xs text-charcoal-gray mb-3">
                      {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                    </p>
                    <div className="space-y-3">
                      <div className="bg-emerald-500/10 p-3 rounded-xl border border-emerald-500/20">
                        <p className="text-xs text-emerald-300/80 mb-1">Deposits</p>
                        <p className="text-sm font-bold text-emerald-400">
                          {formatBTC(day.deposits || 0)} BTC
                        </p>
                      </div>
                      <div className="bg-sky-500/10 p-3 rounded-xl border border-sky-500/20">
                        <p className="text-xs text-sky-300/80 mb-1">Withdrawals</p>
                        <p className="text-sm font-bold text-sky-400">
                          {formatBTC(day.withdrawals || 0)} BTC
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h2 className="text-xl font-bold text-white mb-8">Quick Stats</h2>
            <div className="space-y-4">
              {quickStatsItems.map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-3 hover:bg-gray-900/40 rounded-xl transition-colors group"
                >
                  <div className="flex items-center">
                    <div className={`p-2 rounded-lg ${item.color.replace('text-', 'bg-')}/10 border ${item.color.replace('text-', 'border-')}/20`}>
                      <div className={item.color}>
                        {item.icon}
                      </div>
                    </div>
                    <span className="ml-3 text-gray-300 group-hover:text-white">{item.label}</span>
                  </div>
                  <span className="font-bold text-white">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* KYC Status */}
          {stats?.kyc && (
            <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-8">KYC Overview</h2>
              <div className="space-y-4">
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                  <div className="flex items-center">
                    <Shield className="w-5 h-5 text-blue-400 mr-3" />
                    <span className="text-gray-300">Total Submissions</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-400 mt-2">{stats.kyc.total || 0}</p>
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-center">
                    <p className="text-xs text-amber-300/80 mb-1">Pending</p>
                    <p className="text-lg font-bold text-amber-400">{stats.kyc.pending || 0}</p>
                  </div>
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 text-center">
                    <p className="text-xs text-emerald-300/80 mb-1">Verified</p>
                    <p className="text-lg font-bold text-emerald-400">{stats.kyc.verified || 0}</p>
                  </div>
                  <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-3 text-center">
                    <p className="text-xs text-rose-300/80 mb-1">Rejected</p>
                    <p className="text-lg font-bold text-rose-400">{stats.kyc.rejected || 0}</p>
                  </div>
                </div>
                
                {stats.kyc.avg_processing_hours > 0 && (
                  <div className="pt-4 border-t border-gray-800">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-charcoal-gray">Avg Processing Time</span>
                      <span className="font-medium text-white">{stats.kyc.avg_processing_hours} hours</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* System Status */}
          {stats?.server && (
            <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-8">System Status</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-900/50 p-4 rounded-xl">
                  <div className="flex items-center mb-2">
                    <Cpu className="w-4 h-4 text-sky-400 mr-2" />
                    <p className="text-sm text-charcoal-gray">Server Time</p>
                  </div>
                  <p className="font-bold text-white">
                    {stats.server.server_time.split(' ')[1]}
                  </p>
                </div>
                <div className="bg-gray-900/50 p-4 rounded-xl">
                  <div className="flex items-center mb-2">
                    <Database className="w-4 h-4 text-violet-400 mr-2" />
                    <p className="text-sm text-charcoal-gray">PHP Version</p>
                  </div>
                  <p className="font-bold text-white">{stats.server.php_version}</p>
                </div>
                <div className="bg-gray-900/50 p-4 rounded-xl">
                  <div className="flex items-center mb-2">
                    <MemoryStick className="w-4 h-4 text-emerald-400 mr-2" />
                    <p className="text-sm text-charcoal-gray">Memory Usage</p>
                  </div>
                  <p className="font-bold text-white">{stats.server.memory_usage}</p>
                </div>
                <div className="bg-gray-900/50 p-4 rounded-xl">
                  <div className="flex items-center mb-2">
                    <HardDrive className="w-4 h-4 text-amber-400 mr-2" />
                    <p className="text-sm text-charcoal-gray">Database Size</p>
                  </div>
                  <p className="font-bold text-white">{stats.server.database_size}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Top Depositors */}
      {stats?.wallet?.top_depositors && stats.wallet.top_depositors.length > 0 && (
        <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-white mb-8">Top Depositors (30 Days)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {stats.wallet.top_depositors.map((depositor, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border border-gray-800 rounded-2xl p-5 hover:bg-gray-800/50 transition-all duration-300 hover:scale-[1.02]"
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-lg">
                      {depositor.username?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="ml-3">
                    <p className="font-bold text-white truncate max-w-[120px]">
                      {depositor.username || 'User'}
                    </p>
                    <p className="text-sm text-charcoal-gray truncate max-w-[120px]">{depositor.email}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-charcoal-gray">Total Deposits</p>
                  <p className="text-2xl font-bold bg-gradient-to-r from-emerald-400 to-green-500 bg-clip-text text-transparent">
                    {formatBTC(depositor.total_deposits)} BTC
                  </p>
                  <p className="text-sm text-charcoal-gray">{depositor.deposit_count} deposit(s)</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;